import React, { useState } from 'react';

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/**
 * Modal form for creating a new claim topic.
 * Follows the same pattern as OrgForm.js.
 */
export default function ClaimForm({ orgs = [], onSave, onClose }) {
  const [form, setForm] = useState({
    title: '',
    summary: '',
    tags: [],
    relatedOrgs: [],
    published: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [tagInput, setTagInput] = useState('');

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleOrg = (orgId) => {
    set('relatedOrgs',
      form.relatedOrgs.includes(orgId)
        ? form.relatedOrgs.filter(id => id !== orgId)
        : [...form.relatedOrgs, orgId]
    );
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      set('tags', [...form.tags, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag) => set('tags', form.tags.filter(t => t !== tag));

  const filteredOrgs = orgSearch.trim()
    ? orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()))
    : orgs.slice(0, 20);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.summary.trim()) { setError('Summary is required'); return; }

    setSaving(true);
    setError('');
    try {
      const slug = slugify(form.title);
      const data = {
        ...form,
        id: slug,
        slug: slug,
        published: form.published ? 1 : 0,
      };
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create claim');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>New Claim Topic</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="admin-error">{error}</div>}

            <label className="admin-label">Title *</label>
            <input
              className="admin-input"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g. Forty beheaded babies"
            />
            {form.title && (
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>
                Slug: {slugify(form.title)}
              </div>
            )}

            <label className="admin-label">Summary *</label>
            <textarea
              className="admin-input"
              value={form.summary}
              onChange={e => set('summary', e.target.value)}
              placeholder="Brief summary of the claim and its significance..."
              rows={4}
            />

            {/* Tags */}
            <label className="admin-label">Tags</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {form.tags.map(tag => (
                <span key={tag} className="admin-tag-option active" onClick={() => removeTag(tag)} style={{ cursor: 'pointer' }}>
                  {tag} ×
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="admin-input"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag..."
                style={{ flex: 1 }}
              />
              <button type="button" className="admin-btn-add" onClick={addTag}>Add</button>
            </div>

            {/* Related orgs */}
            <label className="admin-label">Related organisations</label>
            <input
              className="admin-input"
              value={orgSearch}
              onChange={e => setOrgSearch(e.target.value)}
              placeholder="Search organisations..."
              style={{ marginBottom: '0.5rem' }}
            />
            <div className="admin-org-picker">
              {filteredOrgs.map(o => (
                <button
                  key={o.id}
                  type="button"
                  className={`admin-tag-option ${form.relatedOrgs.includes(o.id) ? 'active' : ''}`}
                  onClick={() => toggleOrg(o.id)}
                >
                  {o.name}
                </button>
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '0.85rem', color: '#ccc', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.published}
                onChange={e => set('published', e.target.checked)}
              />
              Published (visible on site)
            </label>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create claim'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
